/**
 * @component ry-form
 * @category  form
 * @summary   Form orchestrator with full Fomantic-UI validation parity. Wires a declarative rule engine, submit handling, field-level state application, value collection, reset, and form-level message/loading states.
 *
 * Rule syntax (on each `<ry-field>` via `[validate]`):
 *   `validate="required, email, minLength:8"`
 *
 * Built-in rules: `required`, `empty`, `notEmpty`, `email`, `url`, `integer[:min..max]`,
 * `decimal[:min..max]`, `number[:min..max]`, `regExp:/pattern/flags`,
 * `minLength:N`, `maxLength:N`, `exactLength:N`, `length:N`,
 * `creditCard`, `match:[name]`, `different:[name]`,
 * `is:[value]`, `isExactly:[value]`, `not:[value]`, `notExactly:[value]`,
 * `contains:[substring]`, `containsExactly:[substring]`,
 * `doesntContain:[substring]`, `doesntContainExactly:[substring]`,
 * `checked`, `minValue:N`, `maxValue:N`.
 *
 * Custom rules can be registered via `RyForm.registerRule(name, fn)`.
 *
 * @attribute size           string?   Cascades a default size to descendant `<ry-field>` elements via `--ry-form-size-scale` token.
 * @attribute inverted       boolean   Dark variant for use on dark backgrounds.
 * @attribute loading        boolean   Overlays a spinner, disables all inner controls.
 * @attribute error          string?   Top-level error message (rendered in the message slot).
 * @attribute success        string?   Top-level success message.
 * @attribute warning        string?   Top-level warning message.
 * @attribute message        string?   The text for the active state message (used with [error]/[success]/[warning]).
 * @attribute validate-on    string?   default=submit   When to run validation: submit | blur | change | input.
 * @attribute action         string?   When present and form is valid, native form submission proceeds.
 * @attribute method         string?   default=post     Native form method (proxied to inner <form>).
 *
 * @property values     Record<string, unknown>   All field values keyed by name (read-only getter).
 * @property valid      boolean                   Result of the last `.validate()` (or true if not yet validated).
 *
 * @method validate()                                       { valid, errors }  — runs all rules, applies field state, returns shape.
 * @method reset()                                          void               — clears all field values + state.
 * @method setRules(fieldName, rules)                       void               — programmatic rule registration.
 * @method addError(fieldName, message)                     void               — push a server-side error onto a field.
 * @method clearErrors()                                    void               — clear ALL field errors.
 * @method static registerRule(name, fn)                    void               — register a custom validation rule globally.
 * @method static setMessages(map)                          void               — override default error message templates.
 *
 * @event ry:submit
 *        detail: { values, valid, errors }
 *        fires:  on submit-button click or Enter-in-field. preventDefault is called when invalid; consumers may also call preventDefault() in their handler to suppress native submission.
 *        cancelable: preventDefault() suppresses the native submission (the `[action]` form.submit() does not run) — the cancelable-under-its-plain-name exception, see ARCHITECTURE.md
 *
 * @event ry:valid
 *        detail: { values }
 *        fires:  after a successful validate() pass.
 *
 * @event ry:invalid
 *        detail: { errors, values }
 *        fires:  after a failed validate() pass.
 *
 * @event ry:beforereset
 *        detail: {}
 *        fires:  at the top of reset(), before any control value, error, or state is touched
 *        cancelable: preventDefault() leaves every value and field state as-is; `ry:reset` does not fire
 *
 * @event ry:reset
 *        detail: {}
 *        fires:  after reset() completes.
 *
 * @keyboard Enter — submits the form (native behavior preserved).
 *
 * @gotcha   The rule string syntax uses `:` to separate name from value, and `,` to separate rules. To include `:` or `,` in a rule value (e.g. `is:[name with, comma]`), wrap the value in `[…]`: `is:[my, value]`. This matches Fomantic's rule-prompt convention.
 *
 * @gotcha   `regExp:/pattern/flags` requires the `/…/` wrapping. Without it, the entire string is treated as a literal pattern. With it, flags are parsed.
 *
 * @gotcha   `match:[name]` and `different:[name]` reference ANOTHER field's name attribute. Cross-field rules re-validate when either side changes (only when `validate-on=change|input|blur`).
 *
 * @gotcha   `[loading]` overlays a spinner AND disables all controls — but the overlay is positioned via the form's inner wrapper; you must NOT also rely on `pointer-events: none` from custom CSS or you'll double-disable. Trust the component.
 *
 * @gotcha   `ry:submit` fires even when invalid (with `detail.valid=false`). Consumers usually only act on `detail.valid=true`. The native submission only proceeds if the form has `[action]` set AND validation passed.
 *
 * @gotcha   If you do NOT pass a `[validate]` attribute to a `<ry-field>`, the field is excluded from validation (but still included in `.values`). Use `validate="required"` for a no-op required check.
 *
 * @example basic
 * ```html
 * <ry-form>
 *   <ry-field label="Email" validate="required, email">
 *     <input type="email" name="email">
 *   </ry-field>
 *   <ry-button type="submit" variant="primary">Submit</ry-button>
 * </ry-form>
 * ```
 *
 * @example sizes-cascade
 * ```html
 * <ry-form size="large">
 *   <ry-field label="First"><input type="text" name="first"></ry-field>
 *   <ry-field label="Last"><input type="text" name="last"></ry-field>
 *   <ry-button type="submit">Save</ry-button>
 * </ry-form>
 * ```
 *
 * @example loading-state
 * ```html
 * <ry-form loading>
 *   <ry-field label="Email"><input type="email"></ry-field>
 *   <ry-button type="submit">Saving…</ry-button>
 * </ry-form>
 * ```
 *
 * @example error-message
 * ```html
 * <ry-form error message="Please fix the errors below.">
 *   <ry-field label="Email" error="Required"><input type="email"></ry-field>
 * </ry-form>
 * ```
 *
 * @example success-message
 * ```html
 * <ry-form success message="Your changes were saved.">
 *   <ry-field label="Email"><input type="email" value="me@x.com"></ry-field>
 * </ry-form>
 * ```
 *
 * @example cross-field-match
 * ```html
 * <ry-form>
 *   <ry-field label="Password" validate="required, minLength:8">
 *     <input type="password" name="password">
 *   </ry-field>
 *   <ry-field label="Confirm" validate="required, match:[password]">
 *     <input type="password" name="confirm">
 *   </ry-field>
 *   <ry-button type="submit">Sign up</ry-button>
 * </ry-form>
 * ```
 *
 * @example validate-on-blur
 * ```html
 * <ry-form validate-on="blur">
 *   <ry-field label="Email" validate="required, email"><input name="email" type="email"></ry-field>
 *   <ry-button type="submit">Submit</ry-button>
 * </ry-form>
 * ```
 *
 * @example inverted
 * ```html
 * <ry-form inverted>
 *   <ry-field label="Email"><input type="email" name="email"></ry-field>
 *   <ry-button type="submit">Send</ry-button>
 * </ry-form>
 * ```
 *
 * @tutorial validate-required
 * ```js
 * const el = mount('<ry-form><ry-field validate="required" label="Name"><input name="n"></ry-field></ry-form>');
 * const result = el.validate();
 * await expectEqual(result.valid, false);
 * await expectEqual(typeof result.errors.n, 'string');
 * ```
 *
 * @tutorial validate-email
 * ```js
 * const el = mount('<ry-form><ry-field validate="email" label="Email"><input name="e" value="not-an-email"></ry-field></ry-form>');
 * const r = el.validate();
 * await expectEqual(r.valid, false);
 * el.querySelector('input').value = 'me@x.com';
 * await expectEqual(el.validate().valid, true);
 * ```
 *
 * @tutorial reset-clears-values-and-state
 * ```js
 * const el = mount('<ry-form><ry-field validate="required" label="x"><input name="x" value="hi"></ry-field></ry-form>');
 * el.reset();
 * await expectEqual(el.querySelector('input').value, '');
 * ```
 *
 * @tutorial beforereset-veto
 * ```js
 * // preventDefault() on ry:beforereset keeps every value and error in place
 * // and ry:reset stays silent; without the veto reset() clears the form.
 * const el = mount('<ry-form><ry-field validate="required" label="x"><input name="x" value="hi"></ry-field></ry-form>');
 * const input = el.querySelector('input');
 * input.value = 'typed';
 * el.addError('x', 'Server said no');
 * const resets = [];
 * el.addEventListener('ry:reset', () => resets.push(1));
 * el.addEventListener('ry:beforereset', (e) => e.preventDefault(), { once: true });
 * el.reset();
 * await expectEqual(input.value, 'typed', 'vetoed reset leaves the value');
 * await expectAttr(el.querySelector('ry-field'), 'error', 'Server said no');
 * await expectEqual(resets.length, 0, 'ry:reset did not fire');
 * el.reset();
 * await expectEqual(input.value, 'hi', 'without the veto, reset() restores the default');
 * await expectEqual(resets.length, 1);
 * ```
 *
 * @see ry-field, ry-fields
 */

import { RyElement } from '../core/ry-element.js';

// ─────────────────────────────────────────────────────────────────────
// Rule engine
// ─────────────────────────────────────────────────────────────────────

/** A validation rule function. Returns true if valid. */
export type RuleFn = (
  value: unknown,
  ruleValue: string | undefined,
  ctx: RuleContext
) => boolean;

export interface RuleContext {
  /** The form element running the validation. */
  form: RyForm;
  /** The field element being validated. */
  field: HTMLElement;
  /** The control name (for cross-field lookups). */
  name: string;
}

interface ParsedRule {
  name: string;
  /** The raw rule-value string (after `:` or inside `[…]`). */
  value: string | undefined;
}

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const URL_RE = /^(https?:\/\/)?([\w.-]+)\.([a-z.]{2,})(\/.*)?$/i;
const INTEGER_RE = /^-?\d+$/;
const DECIMAL_RE = /^-?\d*\.?\d+$/;
const NUMBER_RE = /^-?\d*\.?\d+(?:[eE][+-]?\d+)?$/;

/** Parse `"required, email, minLength:8, regExp:/x/i"` into structured rules. */
export function parseRules(input: string): ParsedRule[] {
  if (!input?.trim()) return [];
  const out: ParsedRule[] = [];
  // Split on top-level commas (commas inside [...] are escaped from the split).
  let depth = 0;
  let buf = '';
  for (const ch of input) {
    if (ch === '[') depth++;
    if (ch === ']') depth--;
    if (ch === ',' && depth === 0) {
      if (buf.trim()) out.push(parseSingleRule(buf.trim()));
      buf = '';
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) out.push(parseSingleRule(buf.trim()));
  return out;
}

function parseSingleRule(raw: string): ParsedRule {
  const colon = raw.indexOf(':');
  if (colon === -1) return { name: raw, value: undefined };
  const name = raw.slice(0, colon).trim();
  let value = raw.slice(colon + 1).trim();
  if (value.startsWith('[') && value.endsWith(']')) {
    value = value.slice(1, -1);
  }
  return { name, value };
}

const BUILTIN_RULES: Record<string, RuleFn> = {
  required: (v) => v !== null && v !== undefined && String(v).trim().length > 0,
  notEmpty: (v) => v !== null && v !== undefined && String(v).trim().length > 0,
  empty: (v) => v === null || v === undefined || String(v).trim().length === 0,
  checked: (v) => v === true || v === 'on' || v === 'true',
  email: (v) => !v || EMAIL_RE.test(String(v)),
  url: (v) => !v || URL_RE.test(String(v)),
  integer: (v, rv) => {
    const s = String(v ?? '');
    if (!s) return true;
    if (!INTEGER_RE.test(s)) return false;
    if (rv) return checkNumericRange(Number(s), rv);
    return true;
  },
  decimal: (v, rv) => {
    const s = String(v ?? '');
    if (!s) return true;
    if (!DECIMAL_RE.test(s)) return false;
    if (rv) return checkNumericRange(Number(s), rv);
    return true;
  },
  number: (v, rv) => {
    const s = String(v ?? '');
    if (!s) return true;
    if (!NUMBER_RE.test(s)) return false;
    if (rv) return checkNumericRange(Number(s), rv);
    return true;
  },
  regExp: (v, rv) => {
    if (!v || !rv) return true;
    const m = rv.match(/^\/(.*)\/([a-z]*)$/i);
    let re: RegExp;
    try {
      re = m ? new RegExp(m[1] ?? '', m[2] ?? '') : new RegExp(rv);
    } catch {
      return false;
    }
    return re.test(String(v));
  },
  minLength: (v, rv) => String(v ?? '').length >= Number(rv ?? 0),
  maxLength: (v, rv) => String(v ?? '').length <= Number(rv ?? 0),
  exactLength: (v, rv) => String(v ?? '').length === Number(rv ?? 0),
  length: (v, rv) => String(v ?? '').length === Number(rv ?? 0),
  minValue: (v, rv) => {
    const n = Number(v ?? '');
    return Number.isNaN(n) ? true : n >= Number(rv ?? 0);
  },
  maxValue: (v, rv) => {
    const n = Number(v ?? '');
    return Number.isNaN(n) ? true : n <= Number(rv ?? 0);
  },
  is: (v, rv) => !v || String(v).toLowerCase() === String(rv ?? '').toLowerCase(),
  isExactly: (v, rv) => !v || String(v) === String(rv ?? ''),
  not: (v, rv) => !v || String(v).toLowerCase() !== String(rv ?? '').toLowerCase(),
  notExactly: (v, rv) => !v || String(v) !== String(rv ?? ''),
  contains: (v, rv) => !v || String(v).toLowerCase().includes(String(rv ?? '').toLowerCase()),
  containsExactly: (v, rv) => !v || String(v).includes(String(rv ?? '')),
  doesntContain: (v, rv) => !v || !String(v).toLowerCase().includes(String(rv ?? '').toLowerCase()),
  doesntContainExactly: (v, rv) => !v || !String(v).includes(String(rv ?? '')),
  match: (v, rv, ctx) => {
    if (!rv) return true;
    const other = ctx.form.getValue(rv);
    return String(v ?? '') === String(other ?? '');
  },
  different: (v, rv, ctx) => {
    if (!rv) return true;
    const other = ctx.form.getValue(rv);
    return String(v ?? '') !== String(other ?? '');
  },
  creditCard: (v) => {
    if (!v) return true;
    const digits = String(v).replace(/\D/g, '');
    if (digits.length < 12 || digits.length > 19) return false;
    // Luhn
    let sum = 0;
    let alt = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      const ch = digits[i];
      if (ch === undefined) return false;
      let n = Number(ch);
      if (Number.isNaN(n)) return false;
      if (alt) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      sum += n;
      alt = !alt;
    }
    return sum % 10 === 0;
  },
};

function checkNumericRange(n: number, rv: string): boolean {
  // "5..10", "5..", "..10"
  const m = rv.match(/^(-?\d+(?:\.\d+)?)?\.\.(-?\d+(?:\.\d+)?)?$/);
  if (m) {
    const min = m[1] !== undefined && m[1] !== '' ? Number(m[1]) : -Infinity;
    const max = m[2] !== undefined && m[2] !== '' ? Number(m[2]) : Infinity;
    return n >= min && n <= max;
  }
  return true;
}

const DEFAULT_MESSAGES: Record<string, string> = {
  required: '{name} is required',
  notEmpty: '{name} must have a value',
  empty: '{name} must be empty',
  checked: '{name} must be checked',
  email: '{name} must be a valid e-mail',
  url: '{name} must be a valid URL',
  integer: '{name} must be an integer',
  decimal: '{name} must be a decimal number',
  number: '{name} must be a number',
  regExp: '{name} is not formatted correctly',
  minLength: '{name} must be at least {ruleValue} characters',
  maxLength: '{name} cannot be longer than {ruleValue} characters',
  exactLength: '{name} must be exactly {ruleValue} characters',
  length: '{name} must be exactly {ruleValue} characters',
  minValue: '{name} must be at least {ruleValue}',
  maxValue: '{name} cannot be greater than {ruleValue}',
  is: '{name} must be "{ruleValue}"',
  isExactly: '{name} must be exactly "{ruleValue}"',
  not: '{name} cannot be "{ruleValue}"',
  notExactly: '{name} cannot be exactly "{ruleValue}"',
  contains: '{name} must contain "{ruleValue}"',
  containsExactly: '{name} must contain exactly "{ruleValue}"',
  doesntContain: '{name} cannot contain "{ruleValue}"',
  doesntContainExactly: '{name} cannot contain exactly "{ruleValue}"',
  match: '{name} must match {ruleValue}',
  different: '{name} must differ from {ruleValue}',
  creditCard: '{name} must be a valid credit card number',
};

const customRules: Record<string, RuleFn> = {};
const customMessages: Record<string, string> = {};

interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

interface ProgrammaticRules {
  [fieldName: string]: string | ParsedRule[];
}

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

export class RyForm extends RyElement {
  static observedAttributes = [
    'size', 'inverted', 'loading',
    'error', 'success', 'warning', 'message',
    'validate-on',
  ] as const;

  /** Programmatically attached rules (overrides per-field [validate] when set). */
  #programmaticRules: Map<string, ParsedRule[]> = new Map();

  /** Server-side errors stacked on top of validation. */
  #manualErrors: Map<string, string> = new Map();

  /** Last-known validity result. */
  #lastValid = true;

  setup(): void {
    // ARIA: role="form" only when there's a label (per ARIA spec).
    if (this.hasAttribute('aria-label') || this.hasAttribute('aria-labelledby')) {
      this.setAttribute('role', 'form');
    }

    this.#renderScaffold();
    this.#syncState();

    // Wire submit-buttons + Enter-in-field
    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);

    // Wire validate-on triggers
    const trigger = this.getAttribute('validate-on') ?? 'submit';
    if (trigger === 'blur') {
      this.on(this, 'focusout', this.#handleFieldEvent);
    } else if (trigger === 'change') {
      this.on(this, 'change', this.#handleFieldEvent);
    } else if (trigger === 'input') {
      this.on(this, 'input', this.#handleFieldEvent);
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.isConnected) return;
    if (name === 'error' || name === 'success' || name === 'warning' || name === 'message') {
      this.#syncMessage();
    } else {
      this.#syncState();
    }
  }

  #renderScaffold(): void {
    // Render an optional message slot at the top of the form
    if (!this.$('[data-ry-target="form-message"]')) {
      const msg = document.createElement('div');
      msg.setAttribute('data-ry-target', 'form-message');
      msg.setAttribute('role', 'alert');
      msg.setAttribute('aria-live', 'polite');
      msg.className = 'ry-form__message';
      this.insertBefore(msg, this.firstChild);
    }
    this.#syncMessage();
  }

  #syncState(): void {
    // Cascading size token
    const size = this.getAttribute('size');
    if (size) {
      const scale = SIZE_SCALE[size];
      if (scale !== undefined) {
        (this.style as CSSStyleDeclaration).setProperty('--ry-form-size-scale', String(scale));
      }
    }
    this.#syncMessage();
  }

  #syncMessage(): void {
    const msg = this.$('[data-ry-target="form-message"]');
    if (!msg) return;
    let state: 'error' | 'success' | 'warning' | null = null;
    if (this.hasAttribute('error')) state = 'error';
    else if (this.hasAttribute('warning')) state = 'warning';
    else if (this.hasAttribute('success')) state = 'success';

    if (state) {
      const text = this.getAttribute('message') ?? this.getAttribute(state) ?? '';
      msg.textContent = text;
      msg.setAttribute('data-ry-state', state);
    } else {
      msg.textContent = '';
      msg.removeAttribute('data-ry-state');
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────────────────────────────

  /** Returns every named field's current value as `{ [name]: value }`. */
  get values(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const { name, value } of this.#iterateControls()) {
      if (!name) continue;
      out[name] = value;
    }
    return out;
  }

  /** Look up a named control's value (used by cross-field rules). */
  getValue(name: string): unknown {
    for (const c of this.#iterateControls()) {
      if (c.name === name) return c.value;
    }
    return undefined;
  }

  /** Reset every control to its initial value, clear errors and state. */
  reset(): void {
    if (!this.emit('beforereset', {})) return;
    this.#manualErrors.clear();
    // Reset native controls via the form-reset path
    const form = this.querySelector('form');
    if (form) form.reset();
    // Plus the manual sweep — covers ry-* custom elements
    for (const c of this.#iterateControls()) {
      const el = c.el;
      if (el instanceof HTMLInputElement) {
        if (el.type === 'checkbox' || el.type === 'radio') {
          el.checked = el.defaultChecked;
        } else {
          el.value = el.defaultValue;
        }
      } else if (el instanceof HTMLTextAreaElement) {
        el.value = el.defaultValue;
      } else if (el instanceof HTMLSelectElement) {
        for (const opt of el.options) opt.selected = opt.defaultSelected;
      } else if ('value' in el) {
        try { (el as unknown as { value: string }).value = ''; } catch { /* ignore */ }
      }
    }
    // Clear all ry-field state
    for (const f of this.#fields()) {
      f.removeAttribute('error');
      f.removeAttribute('success');
      f.removeAttribute('warning');
    }
    this.removeAttribute('error');
    this.removeAttribute('success');
    this.removeAttribute('warning');
    this.removeAttribute('message');
    this.#lastValid = true;
    this.emit('reset', {});
  }

  /** Push a single server-side error onto a named field. */
  addError(name: string, message: string): void {
    this.#manualErrors.set(name, message);
    const field = this.#fieldByName(name);
    field?.setAttribute('error', message);
  }

  /** Clear all field-level errors (visual + manual). */
  clearErrors(): void {
    this.#manualErrors.clear();
    for (const f of this.#fields()) f.removeAttribute('error');
  }

  /** Programmatically attach rules to a named field. */
  setRules(name: string, rules: string | ParsedRule[]): void {
    const parsed = typeof rules === 'string' ? parseRules(rules) : rules;
    this.#programmaticRules.set(name, parsed);
  }

  /** Read the last validation result. */
  get valid(): boolean {
    return this.#lastValid;
  }

  /** Run all rules synchronously, apply field state, return shape. */
  validate(): ValidationResult {
    const errors: Record<string, string> = {};
    // First clear all field errors (so failures from a prior run go away)
    for (const f of this.#fields()) f.removeAttribute('error');

    for (const c of this.#iterateControls()) {
      const { name, value, el, field } = c;
      if (!name) continue;
      const rules = this.#rulesFor(name, field);
      if (rules.length === 0) continue;
      for (const rule of rules) {
        const fn = customRules[rule.name] ?? BUILTIN_RULES[rule.name];
        if (!fn) continue;
        const ok = fn(value, rule.value, { form: this, field: field ?? el, name });
        if (!ok) {
          const msg = formatMessage(rule, this.#friendlyName(name, field));
          errors[name] = msg;
          field?.setAttribute('error', msg);
          break;
        }
      }
    }

    // Layer server-side errors
    for (const [name, msg] of this.#manualErrors) {
      if (!errors[name]) {
        errors[name] = msg;
        this.#fieldByName(name)?.setAttribute('error', msg);
      }
    }

    const valid = Object.keys(errors).length === 0;
    this.#lastValid = valid;

    // Auto error summary at top
    if (!valid) {
      this.setAttribute('error', '');
      const summary = Object.values(errors).join('\n');
      this.setAttribute('message', summary);
      this.emit('invalid', { errors, values: this.values });
    } else {
      // Clear form-level error if it was auto-set
      if (this.hasAttribute('error') && this.getAttribute('message')?.includes('\n')) {
        this.removeAttribute('error');
        this.removeAttribute('message');
      }
      this.emit('valid', { values: this.values });
    }
    return { valid, errors };
  }

  /** Register a custom rule globally. */
  static registerRule(name: string, fn: RuleFn, message?: string): void {
    customRules[name] = fn;
    if (message) customMessages[name] = message;
  }

  /** Override default error message templates. */
  static setMessages(map: Record<string, string>): void {
    Object.assign(customMessages, map);
  }

  // ──────────────────────────────────────────────────────────────────
  // Event handlers
  // ──────────────────────────────────────────────────────────────────

  #handleClick = (e: MouseEvent): void => {
    if (this.hasAttribute('loading')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const target = e.target as HTMLElement | null;
    if (!target) return;
    // Detect submit button: <button type="submit"> OR <ry-button type="submit">
    const submit = target.closest('button[type="submit"], ry-button[type="submit"], [data-ry-submit]');
    if (!submit || !this.contains(submit)) return;
    this.#submit(e);
  };

  #handleKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    // Don't intercept Enter in textarea
    if (target instanceof HTMLTextAreaElement) return;
    if (target instanceof HTMLInputElement || target.tagName.startsWith('RY-')) {
      this.#submit(e);
    }
  };

  #handleFieldEvent = (e: Event): void => {
    // Only re-validate the touched field (cheap)
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const field = target.closest('ry-field') as HTMLElement | null;
    if (!field) return;
    // Validate just this field by name
    const ctrl = this.#iterateControlsList().find(c => c.field === field);
    if (!ctrl || !ctrl.name) return;
    const rules = this.#rulesFor(ctrl.name, field);
    for (const rule of rules) {
      const fn = customRules[rule.name] ?? BUILTIN_RULES[rule.name];
      if (!fn) continue;
      const ok = fn(ctrl.value, rule.value, { form: this, field, name: ctrl.name });
      if (!ok) {
        const msg = formatMessage(rule, this.#friendlyName(ctrl.name, field));
        field.setAttribute('error', msg);
        return;
      }
    }
    field.removeAttribute('error');
  };

  #submit(originalEvent: Event): void {
    const result = this.validate();
    const cancelled = !this.emit('submit', {
      values: this.values,
      valid: result.valid,
      errors: result.errors,
    });
    if (!result.valid || cancelled) {
      originalEvent.preventDefault();
      return;
    }
    // Valid: if there's a native form descendant with [action], let it submit
    const form = this.querySelector('form');
    if (form && this.getAttribute('action')) {
      form.setAttribute('action', this.getAttribute('action') ?? '');
      form.setAttribute('method', this.getAttribute('method') ?? 'post');
      form.submit();
    } else if (!this.getAttribute('action')) {
      // No action — fully handled by the ry:submit consumer
      originalEvent.preventDefault();
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Internals
  // ──────────────────────────────────────────────────────────────────

  #rulesFor(name: string, field: Element | null): ParsedRule[] {
    if (this.#programmaticRules.has(name)) {
      return this.#programmaticRules.get(name) ?? [];
    }
    const attr = field?.getAttribute('validate');
    return attr ? parseRules(attr) : [];
  }

  #fields(): HTMLElement[] {
    return this.$$<HTMLElement>('ry-field');
  }

  #fieldByName(name: string): HTMLElement | null {
    for (const c of this.#iterateControls()) {
      if (c.name === name) return c.field ?? c.el;
    }
    return null;
  }

  #friendlyName(name: string, field: Element | null): string {
    return field?.getAttribute('label') ?? name;
  }

  *#iterateControls(): IterableIterator<ControlEntry> {
    const seen = new Set<HTMLElement>();
    // A radio GROUP is one logical control, not one per input. Track which
    // group names have been emitted so the group yields exactly once — with
    // the checked member's value. Without this, every unchecked sibling
    // overwrites the checked one in `values` and the group reads as undefined.
    const radioGroupsSeen = new Set<string>();
    for (const el of this.querySelectorAll<HTMLElement>(
      'input, textarea, select, ry-checkbox, ry-radio, ry-switch, ry-select, ry-combobox, ry-tag-input, ry-slider, ry-rating, ry-color-input, ry-number-select'
    )) {
      // Skip submit-style buttons
      if (el instanceof HTMLInputElement) {
        if (el.type === 'submit' || el.type === 'button' || el.type === 'reset') continue;
      }
      if (seen.has(el)) continue;
      seen.add(el);
      const name = el.getAttribute('name') ?? '';

      // Named radio group — collapse the whole group into one entry.
      if (el instanceof HTMLInputElement && el.type === 'radio' && name) {
        if (radioGroupsSeen.has(name)) continue;
        radioGroupsSeen.add(name);
        const group = this.$$<HTMLInputElement>('input[type="radio"]')
          .filter((r) => r.getAttribute('name') === name);
        const checked = group.find((r) => r.checked);
        // Anchor the entry on the checked input when there is one, so error
        // state lands on the answered field; otherwise on the first member.
        const anchor = checked ?? el;
        yield {
          name,
          value: checked ? checked.value : undefined,
          el: anchor,
          field: anchor.closest('ry-field') as HTMLElement | null,
        };
        continue;
      }

      const field = el.closest('ry-field') as HTMLElement | null;
      let value: unknown;
      if (el instanceof HTMLInputElement) {
        if (el.type === 'checkbox') value = el.checked;
        else if (el.type === 'radio') value = el.checked ? el.value : undefined;
        else value = el.value;
      } else if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
        value = el.value;
      } else if ('value' in el) {
        value = (el as unknown as { value: unknown }).value;
      } else {
        value = undefined;
      }
      yield { name, value, el, field };
    }
  }

  #iterateControlsList(): ControlEntry[] {
    return [...this.#iterateControls()];
  }
}

interface ControlEntry {
  name: string;
  value: unknown;
  el: HTMLElement;
  field: HTMLElement | null;
}

function formatMessage(rule: ParsedRule, friendlyName: string): string {
  const tpl = customMessages[rule.name] ?? DEFAULT_MESSAGES[rule.name] ?? '{name} is invalid';
  let out = tpl.replace(/\{name\}/g, friendlyName);
  out = out.replace(/\{ruleValue\}/g, rule.value ?? '');
  // {min}/{max} for range rules
  if (rule.value && rule.value.includes('..')) {
    const [min, max] = rule.value.split('..');
    out = out.replace(/\{min\}/g, min ?? '');
    out = out.replace(/\{max\}/g, max ?? '');
  }
  return out;
}

const SIZE_SCALE: Record<string, number> = {
  mini: 0.7,
  tiny: 0.8,
  small: 0.875,
  medium: 1,
  large: 1.125,
  big: 1.25,
  huge: 1.5,
  massive: 1.75,
};

customElements.define('ry-form', RyForm);
